/**
 * localStorage / sessionStorage that cannot take the page down.
 *
 * WHY THIS EXISTS
 * ---------------
 * `localStorage` is not merely "sometimes empty" — accessing it can THROW:
 *
 *   • Chrome/Edge with site data blocked for the origin → SecurityError on the
 *     `window.localStorage` getter itself.
 *   • iOS Safari private mode (and a full quota) → QuotaExceededError on
 *     `setItem`, while `getItem` keeps working.
 *   • Some in-app webviews (notably the Facebook/Instagram browser that paid
 *     social traffic arrives in) partition or disable storage entirely.
 *
 * When one of those throws inside a render or a `useState` initializer, React
 * unwinds to the nearest error boundary. The root layout wraps the WHOLE app in
 * one, so a single unguarded `setItem` replaced the entire site with the
 * boundary's red "Something went wrong" card — no header, no products, no
 * checkout — on every page, with a "Try Again" button that immediately
 * re-threw. Visitors reasonably describe that as a popup they cannot get rid of.
 *
 * Every read and write here is wrapped, and a module-scoped in-memory mirror
 * keeps values usable for the rest of the page view when the real store is
 * unavailable. That turns "site is broken" into "preferences don't persist
 * between visits", which is the correct degradation.
 *
 * Use this instead of touching web storage directly in client components.
 */

const memLocal = new Map<string, string>();
const memSession = new Map<string, string>();

function store(session: boolean): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    // The property access itself can throw — that is the Chrome blocked-data case.
    return session ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function mirror(session: boolean): Map<string, string> {
  return session ? memSession : memLocal;
}

export function safeStorageGet(key: string, session = false): string | null {
  const s = store(session);
  if (s) {
    try {
      const v = s.getItem(key);
      if (v !== null) return v;
    } catch { /* fall through to the mirror */ }
  }
  return mirror(session).get(key) ?? null;
}

export function safeStorageSet(key: string, value: string, session = false): void {
  // Mirror first, so the value is readable even if the real write throws.
  mirror(session).set(key, value);
  const s = store(session);
  if (!s) return;
  try { s.setItem(key, value); } catch { /* quota / private mode — mirror holds it */ }
}

export function safeStorageRemove(key: string, session = false): void {
  mirror(session).delete(key);
  const s = store(session);
  if (!s) return;
  try { s.removeItem(key); } catch { /* nothing more to do */ }
}

/**
 * Can we READ the store at all?
 *
 * Callers need this to tell "the visitor has not answered yet" apart from "we
 * can never find out whether they answered". A guard that treats an unreadable
 * store as "not answered yet" keeps re-asking forever.
 *
 * Deliberately a read probe, not a write probe. iOS Safari private mode and a
 * full quota reject writes while reads keep working: there, a stored answer is
 * still readable, so the guard must keep trusting it. Probing with a write
 * would misreport those visitors as "cannot record consent" and unlock every
 * promotional popup at once — which is exactly the double-popup this caused.
 */
export function storageReadable(session = false): boolean {
  const s = store(session);
  if (!s) return false;
  try {
    s.getItem('__sb_probe__');
    return true;
  } catch {
    return false;
  }
}

/** Convenience wrappers for sessionStorage, same guarantees. */
export const safeSessionGet = (key: string) => safeStorageGet(key, true);
export const safeSessionSet = (key: string, value: string) => safeStorageSet(key, value, true);
export const safeSessionRemove = (key: string) => safeStorageRemove(key, true);
